// Zustand von „Paket anfragen" im Upgrade-Dialog, im Limit-Dialog und in
// UpgradeInline. Der Versand und die Dedupe liegen in pricing-slack.js.
//
// idle, dann sending, danach sent, duplicate (innerhalb von 24 h schon
// angefragt), dry_run (Admin oder Simulation auf Production) oder error. Bei
// error bleibt die Anfrage wiederholbar. Liegt im Browser schon eine Anfrage
// zum selben Gegenstand vor, startet der Hook direkt in duplicate.
//
// pricing-slack.js wird bewusst zuerst geladen, in einer eigenen Gruppe vor
// context. So sind PRICING_REQUEST_STATUS und die Konstanten unten schon
// initialisiert, auch wenn diese Datei mitten im Import-Zyklus über
// context/index.js geladen wird.
import {
  PRICING_REQUEST_RESULTS,
  PRICING_REQUEST_STATUS,
  canWritePartnerLog,
  describePricingRequest,
  isPricingRequestDryRun,
  maybeSendPricingRequest,
  pricingRequestKey,
  wasPricingRequestedRecently
} from './pricing-slack'

import { useCallback, useMemo, useRef, useState } from 'react'

import trackEvents from '../../constants/track-events'
import { usePartnerContext, useUserContext } from '../../context'
import { trackEvent } from '../../helpers/analytics'
import { useEntitlements } from './hooks'

export { PRICING_REQUEST_STATUS }

const STATUS_BY_RESULT = {
  [PRICING_REQUEST_RESULTS.SENT]: PRICING_REQUEST_STATUS.SENT,
  [PRICING_REQUEST_RESULTS.DUPLICATE]: PRICING_REQUEST_STATUS.DUPLICATE,
  [PRICING_REQUEST_RESULTS.DRY_RUN]: PRICING_REQUEST_STATUS.DRY_RUN
}

const DONE = [
  PRICING_REQUEST_STATUS.SENT,
  PRICING_REQUEST_STATUS.DUPLICATE,
  PRICING_REQUEST_STATUS.DRY_RUN
]

/**
 * @param {object} request
 * @param {'FEATURE'|'LIMIT'|'PLAN'|'BILLING'} request.kind
 * @param {string|null} [request.key] Feature-Key oder Ressource
 * @param {string|null} [request.targetPlan] Wunschpaket bei kind PLAN
 * @param {string|null} [request.source] Einstieg, geht nach Slack und ins
 *   Tracking
 * @param {{ resource: string, used: number|null, limit: number|null }|null}
 *   [request.usage] Nutzung für das Feld „Anfrage"
 * @returns {{ status: string, send: () => Promise<string>, reset: () => void,
 *   isSending: boolean, isDone: boolean, isError: boolean,
 *   isDryRun: boolean }}
 */
export const usePricingRequest = ({
  kind,
  key = null,
  targetPlan = null,
  source = null,
  usage = null
} = {}) => {
  const entitlements = useEntitlements()
  const { partner } = usePartnerContext()
  const { cognitoUser, activeSpace, isAdmin } = useUserContext()

  // usage ist bei jedem Render ein neues Objekt, deshalb die Einzelwerte
  const hasUsage = !!usage
  const usageResource = usage?.resource ?? null
  const usageUsed = usage?.used ?? null
  const usageLimit = usage?.limit ?? null

  const descriptor = useMemo(
    () =>
      describePricingRequest({
        kind,
        key,
        targetPlan,
        source,
        usage: hasUsage
          ? { resource: usageResource, used: usageUsed, limit: usageLimit }
          : null,
        partner,
        entitlements,
        cognitoUser,
        activeSpace
      }),
    [
      kind,
      key,
      targetPlan,
      source,
      hasUsage,
      usageResource,
      usageUsed,
      usageLimit,
      partner,
      entitlements,
      cognitoUser,
      activeSpace
    ]
  )

  const requestKey = descriptor.partnerId ? pricingRequestKey(descriptor) : null
  const isDryRun = isPricingRequestDryRun({
    isAdmin,
    isSimulated: descriptor.isSimulated
  })

  const initialStatus = useCallback(
    () =>
      !isDryRun && wasPricingRequestedRecently(descriptor)
        ? PRICING_REQUEST_STATUS.DUPLICATE
        : PRICING_REQUEST_STATUS.IDLE,
    [isDryRun, descriptor]
  )

  const [state, setState] = useState(() => ({
    requestKey,
    status: initialStatus()
  }))

  // Wechselt der Gegenstand (anderes Feature, andere Ressource), beginnt der
  // Zustand neu
  const status =
    state.requestKey === requestKey ? state.status : initialStatus()

  const sendingRef = useRef(false)

  const send = useCallback(async () => {
    if (sendingRef.current) return PRICING_REQUEST_STATUS.SENDING
    sendingRef.current = true
    setState({ requestKey, status: PRICING_REQUEST_STATUS.SENDING })

    // Ohne Partner kann nichts gesendet werden, das ist ein Programmfehler
    if (!descriptor.partnerId) {
      console.error(new Error('pricing request ohne partner_id'))
    }

    let next
    try {
      const result = await maybeSendPricingRequest(descriptor, {
        isAdmin,
        canWriteLog: canWritePartnerLog(cognitoUser, descriptor.partnerId)
      })
      next = STATUS_BY_RESULT[result] || PRICING_REQUEST_STATUS.SENT
    } catch {
      // Webhook-Fehler melden sendSlackMessage und pricing-slack.js selbst an
      // Bugsnag
      next = PRICING_REQUEST_STATUS.ERROR
    }

    sendingRef.current = false
    setState({ requestKey, status: next })

    trackEvent(trackEvents.PRICING_REQUEST_SENT, {
      kind: descriptor.kind,
      key: descriptor.key,
      target_plan: descriptor.targetPlan,
      source: descriptor.source,
      result: next,
      plan: descriptor.plan?.id ?? null,
      mode: descriptor.mode,
      simulated: descriptor.isSimulated
    })

    return next
  }, [requestKey, descriptor, isAdmin, cognitoUser])

  const reset = useCallback(() => {
    setState({ requestKey, status: PRICING_REQUEST_STATUS.IDLE })
  }, [requestKey])

  return {
    status,
    send,
    reset,
    isSending: status === PRICING_REQUEST_STATUS.SENDING,
    isDone: DONE.includes(status),
    isError: status === PRICING_REQUEST_STATUS.ERROR,
    isDryRun
  }
}
